import { FC, useEffect, useMemo, useState } from "react";
import Pagination from "src/components/Pagination/Pagination";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingRecent, {
	type TrackingRecentRow
} from "../hooks/useTrackingRecent";
import { type TrackingEvent } from "../hooks/useTrackingEvents";
import { useEntityResolver } from "../hooks/useEntityResolver";
import { surfaceForEvent, type TrackableSurface } from "../utils/eventNames";

interface Props {
	events: TrackingEvent[];
}

const PAGE_SIZE = 20;
const FETCH_LIMIT = 100;

const EventInspectorTab: FC<Props> = ({ events }) => {
	const [eventName, setEventName] = useState<string>("");
	const recent = useTrackingRecent({
		event: eventName || undefined,
		limit: FETCH_LIMIT,
		refetchInterval: 30000
	});
	const [expanded, setExpanded] = useState<number | null>(null);
	const [page, setPage] = useState(1);

	useEffect(() => {
		setPage(1);
	}, [eventName]);

	// Bucket every recent row's entity_id by the surface its event
	// belongs to so a single round-trip resolves the whole stream's
	// labels regardless of which event types are mixed in it.
	const resolverInput = useMemo(() => {
		const buckets: Partial<Record<TrackableSurface, Set<string>>> = {};
		(recent.data ?? []).forEach((row) => {
			if (!row.entity_id) return;
			const surface = surfaceForEvent(row.event_name);
			if (!surface) return;
			(buckets[surface] ??= new Set<string>()).add(row.entity_id);
		});
		const out: Partial<Record<TrackableSurface, string[]>> = {};
		(Object.keys(buckets) as TrackableSurface[]).forEach((s) => {
			out[s] = Array.from(buckets[s]!);
		});
		return out;
	}, [recent.data]);

	const resolver = useEntityResolver(resolverInput);

	// Surface resolver failures so a broken backend isn't masked
	// behind opaque ids that look like a normal "no match" miss.
	useEffect(() => {
		if (resolver.isError) {
			// eslint-disable-next-line no-console
			console.warn(
				"[EventInspector] Failed to resolve entity titles — events will continue to render with raw ids.",
				resolver.error
			);
		}
	}, [resolver.isError, resolver.error]);

	const labelFor = (row: TrackingRecentRow): string | null => {
		if (!row.entity_id) return null;
		const surface = surfaceForEvent(row.event_name);
		if (!surface) return null;
		return resolver.data?.resolved?.[surface]?.[row.entity_id]?.title ?? null;
	};

	const totalCount = recent.data?.length ?? 0;
	const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
	// Clamp without resetting: if the dataset shrinks below the
	// current page during a poll, fall back to the last available
	// page rather than yanking the user back to page 1 every 30s.
	const safePage = Math.min(page, totalPages);
	const visibleRows = recent.data
		? recent.data.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
		: [];

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<label className="text-sm text-muted-foreground">
					Event:
					<select
						aria-label="Event filter"
						value={eventName}
						onChange={(e) => setEventName(e.target.value)}
						className="ml-2 border border-border rounded-md px-2 py-1 text-sm bg-white"
					>
						<option value="">All events</option>
						{events.map((e) => (
							<option key={e.name} value={e.name}>
								{e.name}
								{e.deprecated_at ? " (deprecated)" : ""}
							</option>
						))}
					</select>
				</label>
				<span className="ml-auto text-xs text-muted-foreground">
					Auto-refreshes every 30s
				</span>
			</div>

			{recent.isLoading ? (
				<div className="h-32 animate-pulse bg-zinc-100 rounded" />
			) : !recent.data || recent.data.length === 0 ? (
				<EmptyState
					title="No recent events"
					description={
						eventName
							? `No events of type ${eventName} in the recent stream.`
							: "Recent stream is empty."
					}
				/>
			) : (
				<div className="rounded-xl border border-border bg-white shadow-sm overflow-hidden">
					<div className="flex items-center justify-between px-4 py-2 border-b border-border bg-subtle">
						<p className="text-sm font-medium text-foreground">
							{totalCount.toLocaleString()}{" "}
							{totalCount === 1 ? "event" : "events"}
							{totalCount >= FETCH_LIMIT ? ` (most recent ${FETCH_LIMIT})` : ""}
						</p>
						<p className="text-xs text-muted-foreground">
							{eventName ? eventName : "All events"}
						</p>
					</div>
					<ul className="divide-y divide-border" aria-label="Recent events">
						{visibleRows.map((row: TrackingRecentRow) => {
							const isOpen = expanded === row.id;
							const detailId = `event-detail-${row.id}`;
							return (
								<li key={row.id} className="px-4 py-2 text-xs font-mono">
									<button
										type="button"
										className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
										aria-expanded={isOpen}
										aria-controls={detailId}
										onClick={() =>
											setExpanded((cur) => (cur === row.id ? null : row.id))
										}
									>
										<span className="text-muted-foreground">
											{row.created_at.replace("T", " ").slice(0, 19)}
										</span>{" "}
										<span className="text-foreground font-medium">
											{row.event_name}
										</span>
										{row.entity_id ? (
											<span className="text-muted-foreground">
												{" "}
												· {labelFor(row) ?? row.entity_id}
											</span>
										) : null}
										{row.created_by_id ? (
											<span className="text-muted-foreground">
												{" "}
												· {row.created_by_id}
											</span>
										) : null}
									</button>
									{isOpen ? (
										<pre
											id={detailId}
											className="mt-2 bg-subtle rounded p-2 overflow-x-auto whitespace-pre-wrap break-all"
										>
											{JSON.stringify(row, null, 2)}
										</pre>
									) : null}
								</li>
							);
						})}
					</ul>
					{totalCount > PAGE_SIZE ? (
						<div className="px-4 pb-3">
							<Pagination
								page={safePage}
								pageSize={PAGE_SIZE}
								totalCount={totalCount}
								onPageChange={setPage}
								itemLabel="events"
							/>
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};

export default EventInspectorTab;
